import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  ApiClient, SanitaerDruckerhoehung, SanitaerGeraet,
} from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe,
  LoadingState, PropertyGrid, StatusBadge, WertEinheit, formatiereZahl, type Spalte, type Zustand,
} from '../muster';
import './sanitaer/sanitaer.css';

/**
 * Die Produktdatenpflege der Sanitärgeräte.
 *
 * --- Die Farben ---------------------------------------------------------------
 *
 * Sie sind keine Anzeigeeinstellung, sondern eine Aussage darüber, wo ein Wert
 * lebt:
 *
 *   HELLGRÜN  Der Wert steht im Modell und kann dorthin zurückgeschrieben
 *             werden. Wer ihn hier ändert, ändert am Ende das Modell.
 *
 *   HELLGELB  Es gibt im Modell keinen Ort dafür. Der Wert lebt nur in der
 *             Datenbank, und ein Modellauszug kennt ihn nicht.
 *
 *   BLAU      Von der Plattform gerechnet. Ihn von Hand zu ändern hieße, das
 *             Ergebnis einer Rechnung durch eine Meinung zu ersetzen.
 *
 *   GRAU      Als falsch erkannt und entwertet — stehen gelassen, damit
 *             nachvollziehbar bleibt, was im Modell stand.
 *
 * Die Farbe kommt vom Server. Sie hier ein zweites Mal abzuleiten wäre die
 * Stelle, an der Farbe und Wahrheit auseinanderlaufen.
 *
 * TS9b1b (TX-STANDARD S1.2/S1.5/S1.8): Die Legende steht als Statusmarken mit Text
 * statt als Farbkästen mit Farbliteralen (grün = gut, gelb = Warnung, blau = Hinweis,
 * grau = neutral - nie nur Farbe); die Seite hat einen `PageHeader` in jedem Zustand.
 *
 * --- Warum die Abweichung eine eigene Spalte hat ------------------------------
 *
 * 263 Klein-Durchlauferhitzer tragen nach Vorgabe 7 kW, im Modell stehen 6,5.
 * Das sind 131,5 kW Unterschied an der Hauptverteilung — und wer nur den
 * gepflegten Wert sieht, erfährt nie davon.
 */

interface Eigenschaften {
  api: ApiClient;
  projektId: string;
}

/** Die vier Herkünfte eines Werts als Statusmarke - dieselbe Bedeutung wie die Farben der Produktseite. */
export const HERKUENFTE: { schluessel: string; zustand: Zustand; was: string }[] = [
  { schluessel: 'gruen', zustand: 'gut', was: 'aus dem Modell, dorthin schreibbar' },
  { schluessel: 'gelb', zustand: 'warnung', was: 'nur in der Datenbank' },
  { schluessel: 'blau', zustand: 'info', was: 'von der Plattform gerechnet' },
  { schluessel: 'grau', zustand: 'neutral', was: 'als falsch erkannt' },
];

const GERAETEARTEN: Record<string, string> = {
  durchlauferhitzer: 'Durchlauferhitzer',
  frischwasserstation: 'Frischwasserstationen',
  hebeanlage: 'Hebeanlagen',
  vorbehaelter: 'Vorbehälteranlagen',
  fettabscheider: 'Fettabscheider',
  enthaertung: 'Aufbereitung',
  pumpe: 'Pumpen',
};

export function SanitaerProdukte({ api, projektId }: Eigenschaften) {
  const [geraete, setGeraete] = useState<SanitaerGeraet[] | null>(null);
  const [dea, setDea] = useState<SanitaerDruckerhoehung[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [versuch, setVersuch] = useState(0);

  useEffect(() => {
    setGeraete(null);
    setFehler(null);
    api.sanitaerGeraete(projektId)
      .then(setGeraete)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));

    // Ein eigener Aufruf und kein Fehler, wenn er scheitert: Die
    // Druckerhöhung ist ein Rechenergebnis. Wer noch nicht gerechnet hat,
    // soll trotzdem seine Geräte sehen.
    api.sanitaerDruckerhoehung(projektId).then(setDea).catch(() => setDea([]));
  }, [api, projektId, versuch]);

  const kopf = (
    <PageHeader
      titel="Produktdaten"
      beschreibung={'Fabrikate und Kennwerte der Sanitärgeräte je Geräteart, mit der Herkunft jedes Werts - '
        + 'dazu die gerechneten Druckerhöhungsanlagen.'}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Die Produktdaten der Sanitärgeräte konnten nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={() => setVersuch((v) => v + 1)}
        />
      </>
    );
  }

  if (!geraete) return <>{kopf}<LoadingState text="Sanitärgeräte abrufen …" zeilen={10} spalten={6} /></>;

  if (geraete.length === 0 && dea.length === 0) {
    return (
      <>
        {kopf}
        <EmptyState
          titel="Für dieses Modell ist noch kein Sanitärgerät aufgenommen."
          erklaerung={'Die Geräte entstehen aus dem Modellauftrag; die Produktdaten '
            + 'werden danach je Typ gepflegt.'}
        />
      </>
    );
  }

  // Nach Geräteart gruppiert und darin nach Produktreihe: 265 Zeilen für
  // 265 gleiche Durchlauferhitzer wären eine Stückliste, keine Datenpflege.
  const arten = [...new Set(geraete.map((g) => g.geraeteart ?? 'ohne'))];

  return (
    <>
      {kopf}
      <KpiReihe>
        <KpiCard titel="Geräte" wert={geraete.length} />
        <KpiCard
          titel="mit Produktreihe"
          wert={geraete.filter((g) => g.produktreihe).length}
          vergleich={`von ${formatiereZahl(geraete.length, 0)} Geräten`}
        />
        <KpiCard
          titel="weichen vom Modell ab"
          wert={geraete.filter((g) => g.abweichend > 0).length}
          zustand={geraete.some((g) => g.abweichend > 0) ? 'warnung' : 'gut'}
          zustandText={geraete.some((g) => g.abweichend > 0)
            ? 'gepflegter Wert ≠ Modellwert' : 'keine Abweichung'}
        />
      </KpiReihe>

      <ContentSection
        titel="Herkunft eines Werts"
        beschreibung={'Die Herkunft eines Wertes steht an ihm: aus dem Modell, nur in '
          + 'der Datenbank, von der Plattform gerechnet oder als falsch erkannt. Sie kommt '
          + 'vom Server — sie hier ein zweites Mal abzuleiten wäre die Stelle, an der '
          + 'Herkunft und Wahrheit auseinanderlaufen.'}
      >
        <div className="san-marken" data-legende>
          {HERKUENFTE.map((h) => <StatusBadge key={h.schluessel} zustand={h.zustand} text={h.was} klein />)}
        </div>
      </ContentSection>

      {dea.length > 0 && <Druckerhoehungen anlagen={dea} />}

      {arten.map((art) => (
        <Artentabelle
          key={art}
          art={art}
          geraete={geraete.filter((g) => (g.geraeteart ?? 'ohne') === art)}
        />
      ))}
    </>
  );
}

/**
 * Die Druckerhöhungsanlagen.
 *
 * Sie stehen vor den Gerätetabellen, weil sie die größte Einzelanlage des
 * Trinkwassernetzes sind — und weil sie bis zuletzt in keiner Liste
 * auftauchten: Die Gerätetabellen zeigen, was modelliert ist, und die
 * Druckerhöhung ist es nicht.
 *
 * Herkunft nach derselben Regel wie die Werte auf der Produktseite: von der
 * Plattform gerechnet.
 */
function Druckerhoehungen({ anlagen }: { anlagen: SanitaerDruckerhoehung[] }) {
  const [offen, setOffen] = useState<string | null>(null);

  const z = (w: number | null, n = 1) => formatiereZahl(w, n);

  const spalten: Spalte<SanitaerDruckerhoehung>[] = [
    {
      schluessel: 'kennung', titel: 'Kennung', immerSichtbar: true,
      wert: (a) => a.kennung,
      inhalt: (a) => <strong>{a.kennung}</strong>,
    },
    { schluessel: 'zone', titel: 'Zone', wert: (a) => a.bezeichnung },
    {
      schluessel: 'produkt', titel: 'Hersteller · Typ',
      wert: (a) => [a.hersteller, a.typ ?? a.baureihe].filter(Boolean).join(' '),
      inhalt: (a) => (
        <>
          {a.hersteller
            ? `${a.hersteller} ${a.typ ?? a.baureihe ?? ''}`.trim()
            : <StatusBadge zustand="warnung" text="Fabrikat offen" klein />}
          {a.pumpen != null && a.betriebspumpen != null && (
            <span className="schwach">
              {' '}· {a.betriebspumpen} Betrieb
              {' '}+ {a.pumpen - a.betriebspumpen} Reserve
            </span>
          )}
        </>
      ),
    },
    {
      schluessel: 'spitze', titel: 'Spitzendurchfluss', art: 'zahl', einheit: 'l/s',
      nachkomma: 2, wert: (a) => a.spitzeLs,
    },
    {
      schluessel: 'foerderstrom', titel: 'Förderstrom', art: 'zahl', einheit: 'm³/h',
      nachkomma: 1, wert: (a) => a.foerderstromM3h,
      inhalt: (a) => (
        <>
          <WertEinheit wert={a.foerderstromM3h} nachkomma={1} rechts />
          {a.foerderstromJePumpeM3h != null && (
            <span className="schwach">
              {' '}({z(a.foerderstromJePumpeM3h, 2)} je Pumpe)
            </span>
          )}
        </>
      ),
    },
    {
      schluessel: 'foerderhoehe', titel: 'Förderhöhe', art: 'zahl', einheit: 'm',
      nachkomma: 1, wert: (a) => a.foerderhoeheM,
      inhalt: (a) => <strong>{z(a.foerderhoeheM)}</strong>,
    },
    { schluessel: 'zapfstellen', titel: 'Zapfstellen', art: 'zahl', wert: (a) => a.zapfstellen },
  ];

  const gewaehlt = anlagen.find((a) => a.kennung === offen) ?? null;

  return (
    <ContentSection
      titel="Druckerhöhungsanlagen"
      meta={`${formatiereZahl(anlagen.length, 0)} Anlagen`}
      beschreibung={'Nicht modelliert, sondern gerechnet: Aus geodätischer Höhe, '
        + 'Mindestfließdruck und Reibung folgt die Förderhöhe, aus dem '
        + 'Spitzendurchfluss die Baugröße. Deshalb kein öffentlicher Verweis — es '
        + 'gibt kein Bauteil im Modell, auf das er zeigen könnte.'}
    >
      <DataTable
        name="Druckerhöhungsanlagen"
        spalten={spalten}
        zeilen={anlagen}
        schluessel={(a) => a.kennung}
        ausfuhr
        ausfuhrDateiname="druckerhoehung"
        beiZeileKlick={(a) => setOffen(offen === a.kennung ? null : a.kennung)}
        aktiveZeile={offen ?? undefined}
        dicht
      />

      {gewaehlt && (
        <ContentSection
          titel={`Rechenweg ${gewaehlt.kennung}`}
          aktionen={(
            <button type="button" className="m-knopf m-knopf-still" onClick={() => setOffen(null)}>
              Schließen
            </button>
          )}
        >
          <PropertyGrid
            spalten={2}
            eintraege={[
              { beschriftung: 'geodätische Höhe', wert: gewaehlt.geodaetischM, einheit: 'm', nachkomma: 2, herkunft: 'baugruppe' },
              { beschriftung: 'Mindestfließdruck', wert: gewaehlt.mindestfliessdruckM, einheit: 'm', nachkomma: 2, herkunft: 'baugruppe' },
              { beschriftung: 'Reibung im Netz', wert: gewaehlt.reibungM, einheit: 'm', nachkomma: 2, herkunft: 'baugruppe' },
              { beschriftung: 'Zuschlag Armaturen', wert: gewaehlt.armaturenM, einheit: 'm', nachkomma: 1, herkunft: 'baugruppe' },
              { beschriftung: 'erforderlich', wert: gewaehlt.erforderlichM, einheit: 'm', nachkomma: 2, herkunft: 'baugruppe' },
              { beschriftung: 'Versorgungsdruck am Eintritt', wert: gewaehlt.versorgungsdruckM, einheit: 'm', nachkomma: 2, herkunft: 'daten' },
              { beschriftung: 'Förderhöhe der Anlage', wert: gewaehlt.foerderhoeheM, einheit: 'm', nachkomma: 2, herkunft: 'baugruppe', breit: true },
            ]}
          />

          {gewaehlt.rechenweg.length > 0 && (
            <ol className="san-rechenweg">
              {gewaehlt.rechenweg.map((x, i) => <li key={i}>{x}</li>)}
            </ol>
          )}
        </ContentSection>
      )}
    </ContentSection>
  );
}

function Artentabelle({ art, geraete }: {
  art: string;
  geraete: SanitaerGeraet[];
}) {
  // Die abweichenden zuerst: Sie sind der Grund, warum jemand diese Seite
  // aufmacht. Der frühere Knopf „alle N zeigen" (Grenze 20) entfällt —
  // `DataTable` virtualisiert und zeigt trotzdem alle (Paket U6b).
  const sortiert = geraete.slice().sort((a, b) =>
    b.abweichend - a.abweichend
    || (a.raumnummer ?? '').localeCompare(b.raumnummer ?? ''));

  const spalten: Spalte<SanitaerGeraet>[] = [
    {
      schluessel: 'stand', titel: 'Stand', art: 'status', breite: '170px',
      wert: (g) => (g.abweichend > 0 ? 'weicht ab'
        : g.produktreihe ? 'gepflegt' : 'ohne Produkt'),
      inhalt: (g) => (g.abweichend > 0
        ? <StatusBadge zustand="warnung" text={`${g.abweichend} weichen ab`} klein />
        : g.produktreihe
          ? <StatusBadge zustand="gut" text="gepflegt" klein />
          : <StatusBadge zustand="neutral" text="ohne Produkt" klein />),
    },
    {
      schluessel: 'typ', titel: 'Typ', immerSichtbar: true, wert: (g) => g.typ,
      inhalt: (g) => (
        <Link to={`../sanitaer/geraete/${g.id}`}>{g.typ ?? '—'}</Link>
      ),
    },
    {
      schluessel: 'produkt', titel: 'Hersteller · Reihe',
      wert: (g) => [g.hersteller, g.produktreihe].filter(Boolean).join(' · '),
      inhalt: (g) => (g.hersteller && g.hersteller !== 'offen'
        ? <>{g.hersteller} · {g.produktreihe}</>
        : (
          <em className="schwach">
            {g.produktreihe ?? 'kein Produkt'}
            {g.hersteller === 'offen' && ' (Fabrikat offen)'}
          </em>
        )),
    },
    { schluessel: 'raum', titel: 'Raum', wert: (g) => g.raumnummer },
    { schluessel: 'geschoss', titel: 'Geschoss', wert: (g) => g.geschoss },
    {
      // Die Adresse - oder der Grund. Nie eine leere Zelle: Ein
      // PAM-GLOBAL-Doppelabzweig bekommt nach F39 NIE eine, ein
      // Durchlauferhitzer ohne Adresse waere eine Luecke. In einer leeren
      // Zelle sind die beiden nicht zu unterscheiden (Paket S8).
      schluessel: 'aks', titel: 'AKS', immerSichtbar: true,
      wert: (g) => g.aksSchluessel ?? g.aksEinstufung,
      inhalt: (g) => (g.aksSchluessel
        ? (
          <span className="san-aks" title={g.aksGrund}>{g.aksSchluessel}</span>
        )
        : (
          <StatusBadge
            zustand={g.aksEinstufung === 'fehlt' ? 'warnung' : 'neutral'}
            text={g.aksEinstufung === 'fehlt' ? 'fehlt' : 'bekommt keine'}
            klein titel={g.aksGrund} />
        )),
    },
    {
      schluessel: 'werte', titel: 'Werte', art: 'zahl', wert: (g) => g.werte,
      inhalt: (g) => (
        <>
          {g.werte}
          {g.abweichend > 0 && (
            <span className="schwach"> ({g.abweichend} abw.)</span>
          )}
        </>
      ),
    },
    {
      schluessel: 'verweis', titel: 'Öffentlicher Verweis', sortierbar: false,
      inhalt: (g) => (g.verweis
        ? (
          <a href={g.verweis} target="_blank" rel="noreferrer"
             onClick={(e) => e.stopPropagation()}>öffnen</a>
        )
        : <span className="schwach">kein Signierschlüssel</span>),
    },
  ];

  return (
    <ContentSection
      titel={GERAETEARTEN[art] ?? 'Ohne Geräteart'}
      meta={`${formatiereZahl(geraete.length, 0)} Geräte`}
      beschreibung={'Der Typ führt auf die Produktdaten zum Pflegen — dieselbe Seite '
        + 'wie bei Heizung, Lüftung und Elektro. Der Verweis rechts führt dorthin '
        + 'ohne Anmeldung; das ist der, der als QR-Code in den Plan geht.'}
      nackt
    >
      <DataTable
        name={GERAETEARTEN[art] ?? 'Ohne Geräteart'}
        spalten={spalten}
        zeilen={sortiert}
        schluessel={(g) => String(g.id)}
        ausfuhr
        ausfuhrDateiname={`sanitaergeraete-${art}`}
        dicht
        leer={(
          <EmptyState
            titel="Keine Geräte dieser Art."
            erklaerung="Die Geräte entstehen aus der Sanitäraufnahme des Modells."
          />
        )}
      />
    </ContentSection>
  );
}
